import { useTranslations } from '@vocab/react';
import { Actions, Box, Button, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  START_RESUME,
  FINISH_RESUME,
  DRAWER_CARDS,
} from 'src/components/Router';
import { Link } from 'src/components/Router/Link';
import { useAppConfig } from 'src/modules/appConfig';
import { useFullyInView } from 'src/modules/hooks/useFullyInView';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import type { Mode, Card, DrawerCard } from 'src/types';

import { Validation } from '../Validation/Validation';
import { updateActiveRoute, updateCreateStep } from '../actionCreators';
import { getScrollParams, resumeLimit } from '../consts';
import { useGenerateResumeFromProfile } from '../data';
import { CreateStep } from '../reducer';
import { useGetCreateResumeRoutes } from '../routes';
import type { CreateResumeRoute } from '../types';

import translations from './.vocab';
import { InvalidSectionButton } from './InvalidSectionButton/InvalidSectionButton';
import { PageErrorMessage } from './PageErrorMessage/PageErrorMessage';

interface PageProps {
  name: CreateResumeRoute;
  marginBottom: React.ReactText;
  resumeCount: number;
  children: React.ReactElement;
}

export const Page = ({
  name,
  marginBottom,
  resumeCount,
  children,
}: PageProps) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const isScrolling = useTypedSelector((store) => store.router.isScrolling);
  const card = useTypedSelector((store) => store.router.latestRoute?.card);
  const mode = useTypedSelector((store) => store.router.latestRoute?.mode);
  const { zone, language: languageCode } = useAppConfig();
  const isEditing =
    mode === 'EDIT' &&
    name === card &&
    !DRAWER_CARDS.includes(card as DrawerCard);

  const createResumeRoutes = useGetCreateResumeRoutes();
  const [generateResumeFromProfile] = useGenerateResumeFromProfile();
  const currentIndex = createResumeRoutes.indexOf(name);
  const isLastPage = currentIndex === createResumeRoutes.length - 1;
  const navigateTo = isLastPage
    ? FINISH_RESUME
    : createResumeRoutes[currentIndex + 1];

  const validPageOnClick = () => {
    if (name === FINISH_RESUME) {
      window.scrollTo(0, 0);

      if (resumeCount < resumeLimit) {
        dispatch(updateCreateStep(CreateStep.RESUME_CREATED));
        setTimeout(
          () => generateResumeFromProfile({ zone, languageCode }),
          500,
        );
      } else {
        dispatch(updateCreateStep(CreateStep.RESUME_LIMIT_REACHED));
      }
    } else {
      dispatch(
        updateActiveRoute({
          route: navigateTo,
          source: 'button',
        }),
      );
    }
  };

  const { ref, inView } = useFullyInView();

  useEffect(() => {
    if (inView && !isScrolling) {
      dispatch(updateActiveRoute({ route: name, source: 'scroll' }));
    }
  }, [dispatch, inView, isScrolling, name]);

  const getButtonText = () => {
    if (name === START_RESUME) {
      return t('Get started');
    }

    if (name === FINISH_RESUME) {
      return t('Create Resumé');
    }

    return t('Next');
  };

  const dataAutomation = `${name}-${
    name === START_RESUME || name === FINISH_RESUME ? '' : 'next-'
  }button`;
  const analytics =
    name === START_RESUME
      ? { action: 'create-resume-get-started-pressed' }
      : {};
  const linkParams = {
    to: navigateTo as Card,
    mode: 'READ' as Mode,
    params: {
      ...getScrollParams(),
      allowScroll: true,
    },
    component: Button,
    color: 'blue',
    onClick: validPageOnClick,
    data: { automation: dataAutomation },
    analytics,
  };
  const buttonText = getButtonText();

  return (
    <Box style={{ marginBottom }} ref={ref}>
      <Stack space="gutter">
        {children}
        {name === FINISH_RESUME ? (
          <Validation renderOnInvalid name={name}>
            <PageErrorMessage />
          </Validation>
        ) : null}
        {name !== START_RESUME ? (
          <>
            {!isEditing && (
              <Actions>
                {name === FINISH_RESUME ? (
                  <>
                    <Validation renderOnInvalid={true} name={name}>
                      <InvalidSectionButton name={name}>
                        {buttonText}
                      </InvalidSectionButton>
                    </Validation>
                    <Validation renderOnInvalid={false} name={name}>
                      <Link {...linkParams}>{buttonText}</Link>
                    </Validation>
                  </>
                ) : (
                  <Link {...linkParams}>{buttonText}</Link>
                )}
              </Actions>
            )}
          </>
        ) : (
          <Actions>
            <Link {...linkParams}>{buttonText}</Link>
          </Actions>
        )}
      </Stack>
    </Box>
  );
};
